import { z } from 'zod';
/**
 * signup#14: the forms a new practice picked during signup (Part One > Consents / Rating Scales and
 * Forms), handed to the EHR at go-live (PUT /api/v1/forms-library/org-picks).
 *
 * SIGNUP_FORM_CATALOG is signup's own list, copied from signup-credify
 * src/frontend/ONBOARDING_PART_ONE_10-27-V50.html (`FORM_NAMED`, `SCREENERS`, `FORM_LIBRARY`,
 * 2026-11-04). A practice can also add a custom form there (id `cust_...`, "Credify builds it").
 *
 * SIGNUP_FORM_SYSTEM_KEY maps a signup id to the Form Builder System Form it is (the credifyfbs
 * `INSTRUMENTS` key the EHR lists as `sys-<key>`, apps/api builtin-form-schemas.ts). Only exact
 * matches (same instrument, same length): a signup form with no System Form is `null` and the EHR
 * reports it as unmatched instead of guessing (AUDIT-C is not the 10-item AUDIT; PHQ-A is not the
 * PHQ-9; the signup "Communication Consent" is not 42 CFR Part 2).
 */
export const SIGNUP_FORM_CATALOG = [
    // the 21 disclosures (consents a practice signs clients on)
    { id: 'consent_treatment', name: 'Informed Consent for Treatment', kind: 'disclosure' },
    { id: 'financial', name: 'Financial Policy', kind: 'disclosure' },
    { id: 'client_rights', name: 'Client Rights and Responsibilities', kind: 'disclosure' },
    {
        id: 'npp_ack',
        name: 'Acknowledgment of Receipt of Notice of Privacy Practices',
        kind: 'disclosure',
    },
    { id: 'esign', name: 'Consent to Use Electronic Records and Signatures', kind: 'disclosure' },
    {
        id: 'roi',
        name: 'Authorization for Release of Protected Health Information',
        kind: 'disclosure',
    },
    { id: 'gfe', name: 'Good Faith Estimate Acknowledgment', kind: 'disclosure' },
    {
        id: 'assign_benefits',
        name: 'Assignment of Benefits and Consent to Bill Insurance',
        kind: 'disclosure',
    },
    { id: 'no_show', name: 'Cancellation and No-Show Policy Acknowledgment', kind: 'disclosure' },
    { id: 'telehealth', name: 'Telehealth Consent', kind: 'disclosure' },
    { id: 'coord_care', name: 'Consent for Coordination of Care', kind: 'disclosure' },
    { id: 'comms', name: 'Communication Consent', kind: 'disclosure' },
    { id: 'emergency', name: 'Emergency Contact Authorization', kind: 'disclosure' },
    { id: 'cc_auth', name: 'Credit Card Authorization', kind: 'disclosure' },
    {
        id: 'ai_listening',
        name: 'Consent to Recording and AI Documentation Tools',
        kind: 'disclosure',
    },
    {
        id: 'media_release',
        name: 'Authorization for Photos, Video, and Testimonials',
        kind: 'disclosure',
    },
    {
        id: 'minor_consent',
        name: 'Parent or Guardian Consent for Treatment of a Minor',
        kind: 'disclosure',
    },
    { id: 'group_agree', name: 'Group Therapy Confidentiality Agreement', kind: 'disclosure' },
    { id: 'supervised', name: 'Supervised Provider Disclosure and Consent', kind: 'disclosure' },
    { id: 'termination', name: 'Termination and Referral Policy', kind: 'disclosure' },
    {
        id: 'court_report',
        name: 'Consent to Report to a Referring Court or Agency',
        kind: 'disclosure',
    },
    // the 15 screeners and assessments
    { id: 'phq9', name: 'PHQ-9 (depression)', kind: 'screener' },
    { id: 'gad7', name: 'GAD-7 (anxiety)', kind: 'screener' },
    { id: 'phqa', name: 'PHQ-A (adolescent depression)', kind: 'screener' },
    { id: 'cssrs', name: 'C-SSRS (suicide risk screen)', kind: 'screener' },
    { id: 'pcl5', name: 'PCL-5 (PTSD)', kind: 'screener' },
    { id: 'audit', name: 'AUDIT (alcohol use)', kind: 'screener' },
    { id: 'dast10', name: 'DAST-10 (drug use)', kind: 'screener' },
    { id: 'asrs', name: 'ASRS (adult ADHD)', kind: 'screener' },
    { id: 'mdq', name: 'MDQ (bipolar)', kind: 'screener' },
    { id: 'scared', name: 'SCARED (child anxiety)', kind: 'screener' },
    { id: 'vanderbilt', name: 'Vanderbilt (child ADHD)', kind: 'screener' },
    { id: 'ace', name: 'ACE (adverse childhood experiences)', kind: 'screener' },
    { id: 'epds', name: 'EPDS (perinatal depression)', kind: 'screener' },
    { id: 'whodas', name: 'WHODAS 2.0 (functioning)', kind: 'screener' },
    { id: 'ors_srs', name: 'ORS / SRS (outcome and session rating)', kind: 'screener' },
    // the library (added with "Add from library")
    { id: 'crafft', name: 'CRAFFT 2.1 (adolescent substance use)', kind: 'screener' },
    { id: 'psc17', name: 'PSC-17 (pediatric symptoms)', kind: 'screener' },
    { id: 'k10', name: 'K10 (psychological distress)', kind: 'screener' },
    { id: 'phq15', name: 'PHQ-15 (physical symptoms)', kind: 'screener' },
    { id: 'isi', name: 'ISI (insomnia)', kind: 'screener' },
    { id: 'gds15', name: 'GDS-15 (geriatric depression)', kind: 'screener' },
    { id: 'auditc', name: 'AUDIT-C (alcohol, short)', kind: 'screener' },
    { id: 'ybocs', name: 'Y-BOCS (OCD)', kind: 'screener' },
    { id: 'snap4', name: 'SNAP-IV (ADHD rating)', kind: 'screener' },
    { id: 'brs', name: 'Brief Resilience Scale', kind: 'screener' },
    { id: 'social_media', name: 'Social media and contact policy', kind: 'disclosure' },
    { id: 'couples', name: 'Couples and family therapy agreement', kind: 'disclosure' },
    { id: 'service_animal', name: 'Service and support animal policy', kind: 'disclosure' },
    { id: 'pet_therapy', name: 'Animal-assisted therapy consent', kind: 'disclosure' },
];
/** Signup id -> Form Builder System Form key (`sys-<key>` in the EHR), or null: no System Form. */
export const SIGNUP_FORM_SYSTEM_KEY = {
    consent_treatment: 'consent_treatment',
    npp_ack: 'hipaa_npp',
    roi: 'roi',
    telehealth: 'consent_telehealth',
    phq9: 'phq9',
    gad7: 'gad7',
    cssrs: 'cssrs',
    pcl5: 'pcl5',
    audit: 'audit',
    dast10: 'dast10',
    ace: 'ace',
    crafft: 'crafft',
    psc17: 'psc17',
};
/** The catalog entry for a signup id, or undefined (a custom form, or an id signup added later). */
export function signupCatalogEntry(id) {
    return SIGNUP_FORM_CATALOG.find((f) => f.id === id);
}
/** The System Form key a signup id maps to, or null (no match: reported, never guessed). */
export function signupSystemKey(id) {
    return Object.prototype.hasOwnProperty.call(SIGNUP_FORM_SYSTEM_KEY, id)
        ? (SIGNUP_FORM_SYSTEM_KEY[id] ?? null)
        : null;
}
const SIGNUP_ID_RE = /^[A-Za-z0-9_.:-]{1,80}$/;
const STATE_RE = /^[A-Z]{2}$/;
export const signupFormPickSchema = z
    .object({
    /** signup's form id (`consent_treatment`, `phq9`, `cust_...`): the idempotency key */
    signupId: z.string().regex(SIGNUP_ID_RE, 'Not a signup form id'),
    kind: z.enum(['disclosure', 'screener']),
    source: z.enum(['catalog', 'library', 'custom']).default('catalog'),
    /** the form's name; needed for a custom form (Credify builds it), else the catalog's */
    name: z.string().trim().max(200).optional(),
    /** a disclosure: the states it is picked in (empty with sameEverywhere = every state) */
    states: z.array(z.string().regex(STATE_RE, 'A 2-letter state')).max(60).default([]),
    sameEverywhere: z.boolean().default(false),
    /** the practice approved it on its counsel's say-so (signup `consents[id]`), or null */
    approval: z
        .object({
        approvedAt: z.string().datetime(),
        termsVersion: z.string().max(40).optional(),
        counselConfirmed: z.boolean().optional(),
    })
        .strict()
        .nullable()
        .default(null),
})
    .strict()
    .superRefine((f, ctx) => {
    if (f.source === 'custom' && !f.name)
        ctx.addIssue({ code: 'custom', path: ['name'], message: 'A custom form needs its name' });
});
/** PUT /api/v1/forms-library/org-picks. The org is the caller's (token), never the body's. */
export const signupFormPicksInputSchema = z
    .object({
    /** signup's application id, kept with each pick for the trail */
    applicationId: z.string().uuid().optional(),
    forms: z.array(signupFormPickSchema).min(1).max(200),
})
    .strict()
    .superRefine((b, ctx) => {
    const seen = new Set();
    b.forms.forEach((f, i) => {
        if (seen.has(f.signupId))
            ctx.addIssue({
                code: 'custom',
                path: ['forms', i, 'signupId'],
                message: 'This form is in the list twice',
            });
        seen.add(f.signupId);
    });
});
//# sourceMappingURL=signupForms.js.map